<template>
  <div class="w-full h-full">
    <div
      v-if="session && !showSpinner"
      class="relative flex flex-col md:flex-row w-full h-full overflow-hidden max-w-[500px] md:max-h-screen md:max-w-none md:bg-[#0d0520]"
    >
      <!-- Row layout for sidebar/drawer/page; unwraps via `contents` on desktop so it
           doesn't affect the existing desktop flex layout. Wrapping is only needed on
           mobile, to make room for the bottom nav bar below it. -->
      <div class="relative flex flex-row flex-1 min-h-0 md:contents">
      <!-- ═══════════════════════════════════════════
           DESKTOP SIDEBAR  (hidden on mobile)
      ════════════════════════════════════════════ -->
      <aside
        :class="[
          'hidden md:flex flex-col flex-shrink-0 h-full z-20 gap-2 transition-all duration-200',
          isDesktopSidebarExpanded ? 'w-60' : 'w-[4.5rem]'
        ]"
      >
        <!-- White card -->
        <div class="bg-white md:bg-gradient-to-b md:from-[#160938] md:to-[#1e0a50] flex flex-col h-full overflow-hidden border-l md:border-none">

          <!-- Logo + collapse toggle -->
          <div class="flex items-center h-[4.5rem] px-3 flex-shrink-0 border-b border-gray-100 md:border-white/10"
               :class="isDesktopSidebarExpanded ? 'justify-between' : 'justify-center'">
            <div v-if="isDesktopSidebarExpanded" class="flex items-center gap-2.5 min-w-0">
              <img
                src="../../assets/img/login/logo-sign.svg"
                class="w-6 h-6 flex-shrink-0 md:brightness-0 md:invert"
                alt="logo"
              />
              <span class="text-sm font-semibold truncate md:text-white/90">{{ businessTitle }}</span>
            </div>
            <button
              class="p-2 rounded-lg hover:bg-gray-100 md:hover:bg-white/10 transition-colors flex-shrink-0"
              @click="isDesktopSidebarExpanded = !isDesktopSidebarExpanded"
            >
              <i :class="['pi text-gray-400 md:text-white/50 text-sm', isDesktopSidebarExpanded ? 'pi-chevron-right' : 'pi-chevron-left']" />
            </button>
          </div>

          <!-- Home button (sticky) -->
          <div class="border-b border-gray-100 md:border-white/10 px-1 py-2 flex-shrink-0">
            <button
              :class="[
                'group relative flex items-center gap-3 w-full px-3 py-2 transition-colors text-sm',
                currentPage === AdminRouteNames.Home
                  ? 'text-primary md:text-white'
                  : 'text-gray-500 hover:text-gray-800 md:text-white/60 md:hover:text-white',
                !isDesktopSidebarExpanded ? 'justify-center' : ''
              ]"
              @click="handleDesktopNavClick(homePageConfig!)"
            >
              <div
                v-if="currentPage === AdminRouteNames.Home && isDesktopSidebarExpanded"
                class="absolute right-0 top-1.5 bottom-1.5 w-0.5 bg-primary md:bg-purple-300 rounded-l-full"
              />
              <span
                :class="[
                  'flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
                  currentPage === AdminRouteNames.Home
                    ? 'bg-primary text-white'
                    : 'text-gray-400 group-hover:bg-gray-100 group-hover:text-gray-600 md:text-white/40 md:group-hover:bg-white/10 md:group-hover:text-white'
                ]"
              >
                <i class="pi pi-home text-sm" />
              </span>
              <span v-if="isDesktopSidebarExpanded" class="flex-1 text-right truncate">דף הבית</span>
              <span
                v-if="!isDesktopSidebarExpanded"
                class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-50"
              >דף הבית</span>
            </button>
          </div>

          <!-- Navigation -->
          <nav class="flex-1 overflow-y-auto py-2 overflow-x-hidden">
            <template v-for="(group, gIdx) in desktopMenuGroups" :key="group.category">
              <!-- Category header -->
              <div
                v-if="isDesktopSidebarExpanded"
                class="px-4 mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-400 md:text-white/30"
              >
                {{ group.category }}
              </div>
              <div v-else-if="gIdx > 0" class="mx-3 border-t border-gray-100 md:border-white/10 my-1.5" />

              <!-- Menu item + optional sub-tabs -->
              <template v-for="item in group.items" :key="item.name">
                <button
                  :class="[
                    'group relative flex items-center gap-3 w-full px-3 py-2 transition-colors text-sm',
                    currentPage === item.name
                      ? 'text-primary md:text-white'
                      : 'text-gray-500 hover:text-gray-800 md:text-white/60 md:hover:text-white',
                    !isDesktopSidebarExpanded ? 'justify-center' : ''
                  ]"
                  @click="handleDesktopNavClick(item)"
                >
                  <!-- Active left indicator (expanded mode) -->
                  <div
                    v-if="currentPage === item.name && isDesktopSidebarExpanded"
                    class="absolute right-0 top-1.5 bottom-1.5 w-0.5 bg-primary md:bg-purple-300 rounded-l-full"
                  />

                  <!-- Icon -->
                  <span
                    :class="[
                      'relative flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
                      currentPage === item.name
                        ? 'bg-primary text-white'
                        : 'text-gray-400 group-hover:bg-gray-100 group-hover:text-gray-600 md:text-white/40 md:group-hover:bg-white/10 md:group-hover:text-white'
                    ]"
                  >
                    <i :class="['pi', item.icon, 'text-sm']" />
                    <span
                      v-if="getMenuBadge(item.name) > 0"
                      class="absolute -top-2 -left-2 min-w-[16px] h-[16px] px-0.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none"
                    >{{ getMenuBadge(item.name) }}</span>
                  </span>

                  <span v-if="isDesktopSidebarExpanded" class="flex-1 text-right truncate">{{ item.title }}</span>

                  <img
                    v-if="isDesktopSidebarExpanded && adminStore.getBusinessSiteData?.plan === Plans.FREE && item.relatedFeature && !adminStore.getIsFeatureAvailable(item.relatedFeature)"
                    src="../../assets/img/admin/premium.png"
                    alt="premium"
                    class="w-4 h-4 flex-shrink-0"
                  />

                  <!-- Tooltip when collapsed -->
                  <span
                    v-if="!isDesktopSidebarExpanded"
                    class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-50"
                  >{{ item.title }}</span>
                </button>

                <!-- Sub-tabs (only when expanded and this page is active) -->
                <div
                  v-if="isDesktopSidebarExpanded && currentPage === item.name && item.tabs?.length"
                  class="flex flex-col mb-1 bg-primary-50 md:bg-white/5 rounded-lg mx-2 py-1"
                >
                  <button
                    v-for="(tab, idx) in item.tabs"
                    :key="tab"
                    :class="[
                      'flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer text-right',
                      activeTabIndex === idx
                        ? 'text-primary md:text-purple-300 font-medium'
                        : 'text-gray-500 hover:text-gray-700 md:text-white/50 md:hover:text-white/80'
                    ]"
                    @click.stop="navigateToTab(item.name, idx)"
                  >
                    {{ tab }}
                    <span
                      v-if="getTabBadge(item.name, idx) > 0"
                      class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none"
                    >{{ getTabBadge(item.name, idx) }}</span>
                  </button>
                </div>
              </template>
            </template>
          </nav>

          <!-- Bottom: referral -->
          <div class="border-t border-gray-100 md:border-white/10 p-2 flex-shrink-0 mt-auto">
            <button
              :class="[
                'group relative flex items-center gap-3 w-full rounded-lg px-3 py-2.5 transition-colors text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 md:text-white/60 md:hover:text-white md:hover:bg-white/10',
                !isDesktopSidebarExpanded ? 'justify-center' : ''
              ]"
              @click="showReferralDialog = true"
            >
              <span class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 group-hover:bg-gray-100 group-hover:text-gray-600 md:text-white/40 md:group-hover:bg-white/10 md:group-hover:text-white">
                <i class="pi pi-gift text-sm" />
              </span>
              <span v-if="isDesktopSidebarExpanded" class="flex-1 text-right truncate">הזמינו חברים וקבלו 75₪</span>
              <span
                v-if="!isDesktopSidebarExpanded"
                class="pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity z-50"
              >הזמינו חברים וקבלו 75₪</span>
            </button>
            <p v-if="isNative" class="text-center text-[11px] text-gray-400 md:text-white/30 mt-1">v{{ appVersion }}</p>
          </div>
        </div>
      </aside>

      <!-- ═══════════════════════════════════════════
           MOBILE RIGHT DRAWER  (hidden on desktop)
      ════════════════════════════════════════════ -->
      <div
        ref="mobileMenuRef"
        :class="{
          'right-drawer md:hidden': true,
          'drawer-open': showMenu,
        }"
      >
        <div
          class="referral-card animate__animated flex-shrink-0"
          :class="{
            'animate__fadeInUp animate__faster': showMenu,
            'animate__fadeOut animate__faster': !showMenu,
          }"
          @click="openReferralFromMobile"
        >
          <div class="referral-card__inner">
            <div class="flex flex-col z-[200] opacity-90 gap-2">
              <span class="font-medium">הזמינו חברים וקבלו 75₪</span>
              <span
                class="text-xs text-pretty pe-12">על כל עסק שיצטרף בהמלצתכם תקבלו שובר ביימי בשווי 75₪ </span>
            </div>
            <img class="absolute w-20 rotate-45 -bottom-5 -left-5" src="../../assets/img/admin/referral-img.png" alt="" />
          </div>
        </div>

        <template v-for="group in mobileMainGroups" :key="group.category">
          <div
            :class="[
              'px-4 mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-white/30 animate__animated',
              showMenu ? 'animate__fadeInUp animate__faster' : 'animate__fadeOut animate__faster',
            ]"
          >
            {{ group.category }}
          </div>
          <template v-for="menu in group.items" :key="menu.name">
            <button
              :id="menu.name"
              :class="[
                'group relative flex items-center gap-3 w-full px-3 py-2 transition-colors text-sm animate__animated',
                showMenu ? 'animate__fadeInUp animate__faster' : 'animate__fadeOut animate__faster',
                currentPage === menu.name
                  ? 'text-white'
                  : 'text-white/60 hover:text-white',
              ]"
              @click="handleMobileMenuClick(menu)"
            >
              <div
                v-if="currentPage === menu.name"
                class="absolute right-0 top-1.5 bottom-1.5 w-0.5 bg-purple-300 rounded-l-full"
              />

              <span
                :class="[
                  'relative flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
                  currentPage === menu.name
                    ? 'bg-primary text-white'
                    : 'text-white/40 group-hover:bg-white/10 group-hover:text-white'
                ]"
              >
                <i :class="['pi', menu.icon, 'text-sm']" />
                <span
                  v-if="getMenuBadge(menu.name) > 0"
                  class="absolute -top-2 -left-2 min-w-[16px] h-[16px] px-0.5 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none"
                >{{ getMenuBadge(menu.name) }}</span>
              </span>

              <span class="flex-1 text-right truncate">{{ menu.title }}</span>

              <img
                v-if="menu.relatedFeature && !adminStore.getIsFeatureAvailable(menu.relatedFeature) && adminStore.getBusinessSiteData?.plan === Plans.FREE"
                src="../../assets/img/admin/premium.png"
                alt="premium"
                class="w-4 h-4 flex-shrink-0"
              />

              <i
                v-if="hasMobileTabs(menu.name)"
                :class="['pi text-xs transition-transform duration-200', mobileExpandedMenu === menu.name ? 'pi-angle-up' : 'pi-angle-down']"
              />
            </button>

            <div
              v-if="mobileExpandedMenu === menu.name && hasMobileTabs(menu.name)"
              class="flex flex-col w-full rounded-lg bg-white/5 mx-2 py-1 mb-1"
              style="width: calc(100% - 1rem)"
            >
              <button
                v-for="(tab, idx) in adminPageConfigMap.get(menu.name)!.tabs"
                :key="tab"
                :class="[
                  'flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer text-right',
                  currentPage === menu.name && activeTabIndex === idx
                    ? 'text-purple-300 font-medium'
                    : 'text-white/50 hover:text-white/80'
                ]"
                @click.stop="goToPathWithTab(menu.name as AdminRouteNames, idx)"
              >
                {{ tab }}
                <span
                  v-if="getTabBadge(menu.name, idx) > 0"
                  class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-red-500 text-white leading-none"
                >{{ getTabBadge(menu.name, idx) }}</span>
              </button>
            </div>
          </template>
        </template>

        <template v-if="mobileHelpGroup">
          <div
            :class="[
              'px-4 mt-4 mb-1 text-[10px] font-bold uppercase tracking-widest text-white/30 animate__animated',
              showMenu ? 'animate__fadeInUp animate__faster' : 'animate__fadeOut animate__faster',
            ]"
          >
            {{ mobileHelpGroup.category }}
          </div>
          <button
            v-for="menu in mobileHelpGroup.items"
            :key="menu.name"
            :id="menu.name"
            :class="[
              'group relative flex items-center gap-3 w-full px-3 py-2 transition-colors text-sm animate__animated',
              showMenu ? 'animate__fadeInUp animate__faster' : 'animate__fadeOut animate__faster',
              currentPage === menu.name
                ? 'text-white'
                : 'text-white/60 hover:text-white',
            ]"
            @click="menu.customAction === 'logout' ? signOut() : menu.customAction === 'contact-us' ? contactUs() : goToPath(menu.name as AdminRouteNames)"
          >
            <span
              :class="[
                'flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
                currentPage === menu.name
                  ? 'bg-primary text-white'
                  : 'text-white/40 group-hover:bg-white/10 group-hover:text-white'
              ]"
            >
              <i :class="['pi', menu.icon, 'text-sm']" />
            </span>
            <span class="flex-1 text-right truncate">{{ menu.title }}</span>
          </button>
        </template>
      </div>

      <!-- ═══════════════════════════════════════════
           MAIN PAGE AREA
      ════════════════════════════════════════════ -->
      <div
        id="page"
        :class="[
          'page md:relative md:flex-1 md:right-auto md:transform-none md:rounded-none md:pointer-events-auto',
          showMenu && 'drawer-open'
        ]"
      >
        <!-- Mobile overlay when drawer is open -->
        <div
          v-if="showMenu"
          class="absolute inset-0 flex justify-center z-[100] pointer-events-auto md:hidden"
          @click="drawerClicked()"
        />

        <!-- ── Header (hidden on the mobile Home page — the calendar shows its own bell) ── -->
        <div
          v-if="!isHeaderlessMobile"
          class="flex items-center w-full h-[4.5rem] bg-white pointer-events-auto z-10 md:border-b flex-shrink-0  border-b border-gray-200"
          @touchstart="pullDisabled = false"
        >
          <!-- Right: back button (child page) or notification bell (mobile top-level) -->
          <div class="flex-none w-14 flex justify-center items-center">
            <i
              v-if="currentPageParent"
              class="pi pi-arrow-right text-lg cursor-pointer px-2 py-4"
              @click="navigateToParent"
            />
            <div
              v-else-if="isMobile"
              class="flex justify-center items-center cursor-pointer"
              @click="openNotifications"
            >
              <OverlayBadge v-if="hasUnviewedNotifications" size="small">
                <i id="open-notifications" class="pi pi-bell text-lg p-2" />
              </OverlayBadge>
              <i v-else id="open-notifications" class="pi pi-bell text-lg px-2 py-4" />
            </div>
          </div>

          <!-- Center: page title -->
          <div class="flex-1 flex justify-center items-center">
            <span class="font-semibold text-base truncate">{{ currentPageTitle }}</span>
          </div>

          <!-- Left: notification bell (desktop) or the current page's header action (mobile) -->
          <div class="flex-none w-14 flex justify-center items-center">
            <div
              v-if="!isMobile"
              class="flex justify-center items-center cursor-pointer"
              @click="openNotifications"
            >
              <OverlayBadge v-if="hasUnviewedNotifications" size="small">
                <i id="open-notifications" class="pi pi-bell text-lg p-2" />
              </OverlayBadge>
              <i v-else id="open-notifications" class="pi pi-bell text-lg px-2 py-4" />
            </div>
            <i
              v-else-if="headerAction"
              :class="['pi', headerAction.icon, 'text-lg text-primary cursor-pointer px-2 py-4']"
              :aria-label="headerAction.ariaLabel"
              @click="headerAction.onClick()"
            />
          </div>
        </div>

        <!-- ── Impersonation bar ── -->
        <div
          v-if="isImpersonating"
          class="flex h-14 px-3 py-1 bg-slate-900 text-white justify-between items-center text-[0.9rem] gap-2 shadow-[rgba(0,0,0,0.05)_0_6px_24px_0,rgba(0,0,0,0.08)_0_0_0_1px]"
        >
          <span class="flex items-center gap-2 min-w-0">
            <i class="pi pi-eye text-amber-300 flex-shrink-0" />
            <span class="truncate">
              מחובר כמנהל לעסק <b>{{ businessTitle }}</b>
              <span v-if="impersonatedByEmail" class="hidden sm:inline text-white/60"> ({{ impersonatedByEmail }})</span>
            </span>
          </span>
          <button
            class="flex-shrink-0 bg-white/15 hover:bg-white/25 transition-colors rounded-lg px-3 py-1.5 font-bold flex items-center gap-1.5 disabled:opacity-60"
            :disabled="stoppingImpersonation"
            @click="stopImpersonation"
          >
            <i :class="['pi', stoppingImpersonation ? 'pi-spin pi-spinner' : 'pi-sign-out', 'text-xs']" />
            יציאה
          </button>
        </div>

        <!-- ── Trial bar ── -->
        <div
          v-if="contractInformation.status === ContractStatus.Trial && showTrialBar"
          class="flex h-14 px-3 py-1 bg-[#54b435] text-white justify-between items-center text-[0.9rem] gap-[0.4rem] shadow-[rgba(0,0,0,0.05)_0_6px_24px_0,rgba(0,0,0,0.08)_0_0_0_1px]"
        >
          <span class="w-full text-center" @click="openTrialModal">
            נותרו עוד {{ daysLeftForTrial }} ימים עד לסוף תקופה הנסיון.
            <span class="underline md:cursor-pointer">לפרטים נוספים</span>
          </span>
          <i class="pi pi-times p-2" @click="showTrialBar = false" />
        </div>

        <!-- ── SMS warning bar ── -->
        <div
          v-if="smsUsage >= 0.95 && showSmsWarning"
          class="flex h-14 px-3 py-1 bg-red-600 text-white justify-between items-center text-[0.9rem] gap-[0.4rem] shadow-[rgba(0,0,0,0.05)_0_6px_24px_0,rgba(0,0,0,0.08)_0_0_0_1px]"
        >
          <span class="w-full text-center">{{
              smsUsage >= 1
                ? 'נגמרו הודעות SMS בחבילה. דברו איתנו ונטפל בזה ביחד 😁'
                : 'לא נשארו עוד הרבה הודעות SMS בחבילה. דברו איתנו ונטפל בזה ביחד 😁'
            }}</span>
          <i class="pi pi-times p-2" @click="hideSmsWarning" />
        </div>

        <!-- ── WhatsApp warning bar ── -->
        <div
          v-if="whatsappUsage >= 0.95 && showWhatsappWarning"
          class="flex h-14 px-3 py-1 bg-red-600 text-white justify-between items-center text-[0.9rem] gap-[0.4rem] shadow-[rgba(0,0,0,0.05)_0_6px_24px_0,rgba(0,0,0,0.08)_0_0_0_1px]"
        >
          <span class="w-full text-center">{{
              whatsappUsage >= 1
                ? 'נגמרו הודעות Whatsapp בחבילה. דברו איתנו ונטפל בזה ביחד 😁'
                : 'לא נשארו עוד הרבה הודעות Whatsapp בחבילה. דברו איתנו ונטפל בזה ביחד 😁'
            }}</span>
          <i class="pi pi-times p-2" @click="hideWhatsappWarning" />
        </div>

        <!-- ── Appointments warning bar ── -->
        <div
          v-if="appointmentsUsage >= 0.85 && showAppointmentsWarning"
          class="flex h-14 px-3 py-1 bg-red-600 text-white justify-between items-center text-[0.9rem] gap-[0.4rem] shadow-[rgba(0,0,0,0.05)_0_6px_24px_0,rgba(0,0,0,0.08)_0_0_0_1px]"
          :style="appointmentsUsage < 1 ? { background: 'orange' } : {}"
        >
          <span class="w-full text-center">{{
              appointmentsUsage >= 1
                ? 'שימו ❤️, הגעתם למכסת התורים לחודש זה. '
                : 'שימו ❤️, אתם מתקרבים למכסת התורים לחודש זה '
            }}</span>
          <i class="pi pi-times p-2" @click="hideAppointmentsLimitWarning" />
        </div>

        <!-- ── Modals / Drawers ── -->
        <Drawer
          v-model:visible="showDownloadApp"
          position="top"
          header="למה אתם מחכים?"
          :pt="{
            header: { style: 'padding: 0.5rem 1rem;' },
            footer: { style: 'padding:0.75rem;' },
          }"
          style="height: fit-content; border-radius: 0"
        >
          <download-our-app />
        </Drawer>

        <Dialog
          v-model:visible="showReferralDialog"
          modal
          rtl
          header="הזמינו חברים וקבלו 75₪"
          :style="{ 'max-height': '85dvh' }"
          :pt="{
            content: { style: 'padding: 0; overflow-y: auto;' },
          }"
        >
          <ReferralDashboard />
        </Dialog>

        <!-- ── Router view ── -->
        <div
          class="bg-white md:bg-gray-50 flex flex-col mt-[-1px] md:mt-0"
          :class="[adminStore.currentPage !== AdminRouteNames.Home ? 'xl:pe-20' : '']"
          :style="{
            height: `calc(100% - ${isHeaderlessMobile ? '0rem' : '4.5rem'} - ${56 * amountOfBars}px)`,
          }"
        >
          <router-view v-slot="{ Component }">
            <component :is="Component" @parent-function="runMethodWithParams" @run-tour="startTour" />
          </router-view>
        </div>
      </div>
      </div>

      <!-- ═══════════════════════════════════════════
           MOBILE BOTTOM NAV BAR  (hidden on desktop)
      ════════════════════════════════════════════ -->
      <!-- No transition on the bg — the page behind it swaps instantly on route
           change, so a fading nav would visibly lag behind it. -->
      <nav
        class="md:hidden flex-shrink-0 flex items-stretch h-[72px] z-[400] border-t -mt-1"
        :class="isDarkNav ? 'bg-[#24104A] border-white/10' : 'bg-white border-gray-200'"
      >
        <button class="flex-1 flex flex-col items-center justify-center gap-1" @click="goToPathFromBottomNav(AdminRouteNames.Home)">
          <i :class="['pi pi-home text-[21px] transition-colors', isBottomHomeActive ? bottomNavActiveTextClass : bottomNavInactiveClass]" />
          <span :class="['text-[10px] transition-colors', isBottomHomeActive ? bottomNavActiveTextClass : bottomNavInactiveClass]">בית</span>
        </button>

        <button class="flex-1 flex flex-col items-center justify-center gap-1" @click="goToPathFromBottomNav(AdminRouteNames.Clients)">
          <i :class="['pi pi-users text-[21px] transition-colors', isBottomClientsActive ? bottomNavActiveTextClass : bottomNavInactiveClass]" />
          <span :class="['text-[10px] transition-colors', isBottomClientsActive ? bottomNavActiveTextClass : bottomNavInactiveClass]">לקוחות</span>
        </button>

        <button v-if="isAIEnabled" class="flex-1 flex flex-col items-center justify-center" @click="openAiFromBottomNav">
          <span class="w-[52px] h-[52px] rounded-xl bg-gradient-to-br from-[#8864ee] to-[#6831d0] shadow-[0_8px_16px_rgba(136,100,238,0.25)] flex flex-col items-center justify-center gap-2">
            <i class="pi pi-sparkles text-white text-lg" />
            <span class="text-[9px] text-white font-semibold leading-none">AI</span>
          </span>
        </button>

        <button class="flex-1 flex flex-col items-center justify-center gap-1" @click="goToPathFromBottomNav(AdminRouteNames.Team)">
          <i :class="['pi pi-box text-[21px] transition-colors', isBottomTeamActive ? bottomNavActiveTextClass : bottomNavInactiveClass]" />
          <span :class="['text-[10px] transition-colors', isBottomTeamActive ? bottomNavActiveTextClass : bottomNavInactiveClass]">צוות</span>
        </button>

        <button class="flex-1 flex flex-col items-center justify-center gap-1" @click="goToPathFromBottomNav(AdminRouteNames.More)">
          <span class="relative">
            <i :class="['pi pi-ellipsis-h text-[21px] transition-colors', isBottomMoreActive ? bottomNavActiveTextClass : bottomNavInactiveClass]" />
            <!-- Something waits behind "עוד": due tasks or smart-nudge suggestions -->
            <span
              v-if="getMenuBadge(AdminRouteNames.Tasks) > 0 || getMenuBadge(AdminRouteNames.Communication) > 0"
              class="absolute -top-0.5 -left-1.5 w-2 h-2 rounded-full bg-red-500"
            />
          </span>
          <span :class="['text-[10px] transition-colors', isBottomMoreActive ? bottomNavActiveTextClass : bottomNavInactiveClass]">עוד</span>
        </button>
      </nav>

      <DynamicDialog />
    </div>

    <!-- Loader fullscreen -->
    <div v-else class="bg-white w-full h-full flex justify-center items-center">
      <loader />
    </div>

    <WhatsNew
      v-if="unseenAnnouncements.length"
      :announcements="unseenAnnouncements"
      :isOpen="showWhatsNew"
      @dismiss="handleAnnouncementsDismiss"
    />


    <WelcomeModal :show="showWelcomeModal" @close="handleWelcomeModalClose" />
    <!-- Caller ID: "customer is calling" card pushed from the owner's Android phone (web only) -->
    <incoming-call-card v-if="!isNativeApp" />
  </div>
</template>

<script lang="ts" setup>
import { IAdminAppNotification, IAnnouncement, IBusinessContract, IMenuItem, Plans } from '@torim-app/types';
import { ContractStatus } from '@torim-app/types';

import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import Session from 'supertokens-web-js/recipe/session';
import { useRoute, useRouter } from 'vue-router';
import DynamicDialog from 'primevue/dynamicdialog';
import { DateTime } from 'luxon';
import { useOneSignal } from '@onesignal/onesignal-vue3';

import { useAdminClientsStore, useAdminHomeStore, useAdminStore } from '@app/stores';
import { useAdminAutomations } from '@app/stores/admin-automations';
import { useAdminTasksStore } from '@app/stores/admin-tasks';
import { useAiAgentStore } from '@app/stores/ai-agent';
import AiAgentDrawer from '@app/components/admin/ai/AiAgentDrawer.vue';
import { adminMenuItems } from '@app/types/admin-menu-items';
import { adminPageConfig, adminPageConfigMap } from '@app/types/admin-page-config';
import { AdminRouteNames, AuthRouteNames, CustomerRouteNames } from '../router/route-names';
import { useResourceUsage } from '@app/composables/resource-usage';
import { SharedApi } from '@app/api/admin';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import OneSignal from 'onesignal-cordova-plugin';
import { useRate } from '@app/composables/rate';
import { Preferences } from '@capacitor/preferences';
import { sendWhatsApp } from '@torim-app/utils';
import TrialInfo from '@app/components/admin/modals/TrialInfo.vue';
import ShopAddProduct from '@app/components/admin/pages/shop-management/ShopAddProduct.vue';
import DownloadOurApp from '@app/components/admin/components/DownloadOurApp.vue';
import Drawer from 'primevue/drawer';

import ReferralDashboard from '@app/components/admin/ReferralDashboard.vue';
import { useAppUpdate } from '@app/composables/app-update';
import VersionUpdateDrawerContent from '@app/components/admin/components/VersionUpdateDrawerContent.vue';
import OverlayBadge from 'primevue/overlaybadge';
import { AnnouncementService } from '@app/api/admin/announcements-api';
import { useStorage } from '@app/composables/use-storage';
import { useDrawer, useSharedStore } from '@torim-app/shared-components';
import { useResponsive } from '@app/composables/responsive';
import { useHeaderAction } from '@app/composables/header-action';
import NotificationsPanel from '@app/components/admin/components/NotificationsPanel.vue';
import { trackEvent, identifyUser, setBusinessGroup, resetPostHog } from '@app/composables/posthog';
import AiFloatingButton from '@app/components/admin/ai/AiFloatingButton.vue';
import { AdminApi } from '@app/api/admin/admin-api';
import { PlatformApi } from '@app/api/platform-api';
import { useProductTour } from '@app/composables/product-tour';
import { useCallerId, onAppointmentsChanged } from '@app/composables/caller-id';
import IncomingCallCard from '@app/components/admin/components/IncomingCallCard.vue';
import { startIncomingCallLive, stopIncomingCallLive } from '@app/composables/incoming-call-live';

const sharedStore = useSharedStore();
const adminStore = useAdminStore();
const adminHomeStore = useAdminHomeStore();
const adminClientsStore = useAdminClientsStore();
const automationsStore = useAdminAutomations();
const tasksStore = useAdminTasksStore();
const aiAgentStore = useAiAgentStore();
const router = useRouter();
const route = useRoute();
const { smsUsage, whatsappUsage, appointmentsUsage, getResourcesUsageData } = useResourceUsage();
const { getValue, setValue } = useStorage();
const { isMobile } = useResponsive();
const { openDrawer, closeDrawer } = useDrawer();
// Active tab index is driven purely by route query: ?tab=N
const activeTabIndex = computed(() => {
  const t = route.query.tab;
  return t !== undefined ? Number(t) : 0;
});

const WELCOME_MODAL_STORAGE_KEY = 'has-seen-welcome-modal';

const persistTourSeen = () => {
  if (adminStore.getSavedStates?.hasSeenProductTour) return;
  AdminApi.updateState({ hasSeenProductTour: true })
    .then(() => adminStore.fetchBusinessData())
    .catch((e) => console.error(e));
};

const { startTour } = useProductTour({
  isMobile: () => isMobile.value,
  getFirstName: () => (adminStore.getBusinessSiteData?.fullName ?? '').trim().split(' ')[0] ?? '',
  navigate: (page, query) => {
    router.push({ name: page, query });
    adminStore.setPage(page);
  },
  setMobileMenu: (open) => {
    showMenu.value = open;
    sharedStore.setIsDrawerOpen(open);
  },
  onFinish: () => persistTourSeen(),
});

// Auto-start the tour once, for a brand-new business owner.
// Gated on: still on the trial plan, hasn't seen the tour before, and not a
// read-only viewer. We intentionally do NOT gate on `isMainClient` — right after
// signup the session token may not carry the main-client/role claim yet, which
// would make the tour silently never appear. The trial-plan check keeps it away
// from established, paying businesses (for whom `hasSeenProductTour` is unset too).
let tourAutoStarted = false;
const maybeAutoStartTour = () => {
  if (tourAutoStarted) return;
  if (adminStore.getBusinessSiteData?.contract?.status !== ContractStatus.Trial) return;
  if (adminStore.getSavedStates?.hasSeenProductTour) return;
  if (adminStore.getIsReadOnly) return;
  tourAutoStarted = true;
  setTimeout(() => startTour(), 600);
};

const { setPage, setIsMainClient, setClientRole, getNotifications } = adminStore;

const pullDisabled = ref(true);
const session = ref(false);
const showMenu = ref(false);
const mobileMenuRef = ref<HTMLElement | null>(null);

// Reset the mobile menu's scroll position to the top each time it opens,
// so reopening after scrolling down doesn't leave it mid-list.
watch(showMenu, (open) => {
  if (open && mobileMenuRef.value) mobileMenuRef.value.scrollTop = 0;
});
const showSpinner = ref(true);
const showSmsWarning = ref(true);
const showWhatsappWarning = ref(true);
const showAppointmentsWarning = ref(true);
const isRefreshClicked = ref(false);
const showReferralDialog = ref(false);
const showTrialBar = ref(true);
const showDownloadApp = ref(false);
const showWhatsNew = ref(false);
const unseenAnnouncements = ref<IAnnouncement[]>([]);
const showWelcomeModal = ref(false);

// ── Platform-admin impersonation state ──
const isImpersonating = ref(false);
const impersonatedByEmail = ref<string | null>(null);
const stoppingImpersonation = ref(false);

provide('refresh', isRefreshClicked);

// ── Desktop sidebar state ──
const isDesktopSidebarExpanded = ref(false);
const isNative = Capacitor.isNativePlatform();
const appVersion = isNative ? __APP_VERSION__ : '';
// ── Mobile drawer expanded menu ──
const mobileExpandedMenu = ref<string | null>(null);

const currentPage = computed<string>(() => adminStore.getCurrentPage);
const notifications = computed<Array<IAdminAppNotification & { _id: string }>>(() => adminStore.notifications);
const hasUnviewedNotifications = computed(() => notifications.value.some(x => !x.viewedAt));
// The mobile Home and More pages hide the shared header (Home renders its own bell in the
// calendar month row; More is a full-bleed dark page). Everything else keeps the header.
const isHeaderlessMobile = computed(() => isMobile.value &&
  (currentPage.value === AdminRouteNames.Home || currentPage.value === AdminRouteNames.More));

// Header action: pages register a leading icon+action; clear it whenever the route changes
// so it never leaks onto the next page (the page re-registers in its own onMounted).
const { headerAction, clearHeaderAction } = useHeaderAction();
watch(() => route.name, () => {
  clearHeaderAction();
  // Auto tasks open in the background (bot handoffs, debts) — keep the badge current.
  tasksStore.fetchCounts();
});
const businessTitle = computed<string>(() => adminStore.getBusinessTitle);
const contractInformation = computed<IBusinessContract>(
  () => adminStore.getBusinessSiteData?.contract ?? ({} as IBusinessContract)
);
const daysLeftForTrial = computed<number>(() => {
  const dateTo = DateTime.fromISO(<string>contractInformation.value.trialEndDate);
  const dateLeft = Math.floor(dateTo.diff(DateTime.local(), 'day').days);
  return dateLeft > 0 ? dateLeft : 0;
});
// Payments page access — any payment integration, invoice integration, or both
const hasPaymentsAccess = computed(() => {
  const integration = adminStore.getBusinessSiteData?.paymentIntegration;
  return !!integration?.paymentConfig?.provider || !!integration?.invoiceConfig?.provider;
});
const filteredMenu = computed(() => {
  return adminMenuItems.filter(x =>
    !x.isSubPage &&
    (!x?.onlyAdmin || (x?.onlyAdmin === true && canManageOthers.value)) &&
    (!x?.requiresPaymentsAccess || hasPaymentsAccess.value)
  );
});
const isMainClient = computed(() => adminStore.getMainClient);
const canManageOthers = computed(() => adminStore.getCanManageOthers);
const canEdit = computed(() => adminStore.getCanEdit);
const isReadOnly = computed(() => adminStore.getIsReadOnly);
const amountOfBars = computed<number>(() => {
  const res = [
    isImpersonating.value,
    contractInformation.value.status === ContractStatus.Trial && showTrialBar.value,
    smsUsage.value >= 0.95 && showSmsWarning.value,
    whatsappUsage.value >= 0.95 && showWhatsappWarning.value,
    appointmentsUsage.value >= 0.85 && showAppointmentsWarning.value
  ].filter(Boolean).length;
  return res;
});

// ── Page config computed ──
const currentPageConfig = computed(() => adminPageConfigMap.get(currentPage.value));
const currentPageTitle = computed(() => {
  if (currentPage.value === AdminRouteNames.HelpCenter) return 'מרכז העזרה';
  return currentPageConfig.value?.title || businessTitle.value;
});
const currentPageParent = computed(() => currentPageConfig.value?.parent);

// ── Mobile bottom nav bar ──
const isAIEnabled = computed(() => adminStore.getBusinessSiteData?.configurations?.adminAiEnabled ?? false);
// The bottom nav goes dark on the More page (and while the legacy tour drawer is open).
const isDarkNav = computed(() => currentPage.value === AdminRouteNames.More || showMenu.value);
const bottomNavInactiveClass = computed(() => isDarkNav.value ? 'text-white/40' : 'text-gray-400');
const bottomNavActiveTextClass = computed(() => isDarkNav.value ? 'text-purple-300 font-medium' : 'text-primary font-medium');
const isBottomHomeActive = computed(() => currentPage.value === AdminRouteNames.Home);
const isBottomClientsActive = computed(() => currentPage.value === AdminRouteNames.Clients || currentPageParent.value === AdminRouteNames.Clients);
const isBottomTeamActive = computed(() => currentPage.value === AdminRouteNames.Team || currentPageParent.value === AdminRouteNames.Team);
const isBottomMoreActive = computed(() => !isBottomHomeActive.value && !isBottomClientsActive.value && !isBottomTeamActive.value);

// ── Desktop menu groups ──
const homePageConfig = computed(() => adminPageConfigMap.get(AdminRouteNames.Home));

const groupMenuItems = (items: typeof adminPageConfig) => {
  const categories: Record<string, typeof items> = {};
  for (const item of items) {
    const cat = item.category || '';
    if (!categories[cat]) categories[cat] = [];
    categories[cat].push(item);
  }
  return Object.entries(categories).map(([category, items]) => ({ category, items }));
};

const desktopMenuGroups = computed(() => {
  return groupMenuItems(adminPageConfig.filter(p => {
    if (!p.isMenuItem) return false;
    if (p.name === AdminRouteNames.Home) return false;
    if (p.onlyAdmin && !canManageOthers.value) return false;
    if (p.requiresPaymentsAccess && !hasPaymentsAccess.value) return false;
    return true;
  }));
});

const mobileMainGroups = computed(() => {
  return groupMenuItems(adminPageConfig.filter(p => {
    if (!p.isMenuItem) return false;
    if (p.category === 'עזרה') return false;
    // The bottom nav already links to Team — don't repeat it in the mobile drawer.
    if (p.name === AdminRouteNames.Team) return false;
    if (p.onlyAdmin && !canManageOthers.value) return false;
    if (p.requiresPaymentsAccess && !hasPaymentsAccess.value) return false;
    return true;
  }));
});

const mobileHelpGroup = computed(() => {
  const items = adminPageConfig.filter(p => p.isMenuItem && p.category === 'עזרה');
  return items.length ? { category: 'עזרה', items } : null;
});

// ── Desktop navigation ──
const handleDesktopNavClick = (item: typeof adminPageConfig[0]) => {
  if (item.customAction === 'logout') {
    signOut();
  } else if (item.customAction === 'contact-us') {
    contactUs();
  } else {
    goToPathDesktop(item.name as AdminRouteNames);
    // If page has tabs and sidebar was collapsed, expand it to show tabs
    if (item.tabs?.length && !isDesktopSidebarExpanded.value) {
      isDesktopSidebarExpanded.value = true;
    }
  }
};

const navigateToTab = (pageName: string, tabIndex: number) => {
  router.push({ name: pageName as AdminRouteNames, query: { tab: String(tabIndex) } });
  setPage(pageName as AdminRouteNames);
};

const getTabBadge = (pageName: string, tabIndex: number): number => {
  // Communication page, tab index 1 = "תזכורת חכמה"
  if (pageName === AdminRouteNames.Communication && tabIndex === 1) {
    return automationsStore.nudgeCount;
  }
  return 0;
};

const getMenuBadge = (pageName: string): number => {
  if (pageName === AdminRouteNames.Communication) {
    return automationsStore.nudgeCount;
  }
  if (pageName === AdminRouteNames.Tasks) {
    return tasksStore.getDueNowCount;
  }
  return 0;
};

const navigateToParent = () => {
  if (currentPageParent.value) {
    router.push({ name: currentPageParent.value });
    setPage(currentPageParent.value);
  }
};

const goToPathDesktop = (pathName: AdminRouteNames) => {
  trackEvent('sidebar_nav_clicked', { page: pathName });
  router.push({ name: pathName });
  setPage(pathName);
};

// The product tour is defined in useProductTour() — see @app/composables/product-tour.ts.
// `startTour` (returned above) is bound to the router-view's `@run-tour` event and
// auto-started for new owners via maybeAutoStartTour().

const drawerClicked = () => {
  const newValue = !showMenu.value;
  showMenu.value = newValue;
  sharedStore.setIsDrawerOpen(newValue);
};

const closeMobileDrawer = () => {
  if (!showMenu.value) return;
  showMenu.value = false;
  sharedStore.setIsDrawerOpen(false);
};

const goToPathFromBottomNav = (pathName: AdminRouteNames, tabIndex?: number) => {
  trackEvent('bottom_nav_clicked', { page: pathName });
  closeMobileDrawer();
  if (tabIndex !== undefined) {
    router.push({ name: pathName, query: { tab: String(tabIndex) } });
  } else {
    router.push({ name: pathName });
  }
  setPage(pathName);
};

const openAiFromBottomNav = () => {
  trackEvent('ai_floating_button_clicked', { source: 'bottom_nav' });
  closeMobileDrawer();
  aiAgentStore.openDrawer();
  openDrawer(AiAgentDrawer, {}, { full: true, closable: true, noPadding: true, bgColor: '#0f0a1e' });
};

const hideSmsWarning = () => {
  showSmsWarning.value = false;
};
const hideWhatsappWarning = () => {
  showWhatsappWarning.value = false;
};
const hideAppointmentsLimitWarning = () => {
  showAppointmentsWarning.value = false;
};

const goToPath = (pathName: AdminRouteNames) => {
  trackEvent('sidebar_nav_clicked', { page: pathName });
  router.push({ name: pathName });
  setPage(pathName);
  drawerClicked();
};

// Both appointment notifications land on the calendar with the appointment highlighted —
// a cancellation is exactly when the owner wants to see the day it freed up.
const APPOINTMENT_NOTIFICATION_TYPES = new Set(['new-appointment', 'appointment-cancelled']);

const handleNotificationTap = (additionalData: any) => {
  if (!additionalData || !APPOINTMENT_NOTIFICATION_TYPES.has(additionalData.type)) return;
  const { appointmentId, appointmentDate, providerId } = additionalData;
  if (!appointmentId) return;
  router.push({
    name: AdminRouteNames.Home,
    query: {
      highlightAppointmentId: appointmentId,
      ...(appointmentDate ? { appointmentDate } : {}),
      ...(providerId ? { providerId } : {}),
    },
  });
  setPage(AdminRouteNames.Home);
};

const openReferralFromMobile = () => {
  trackEvent('referral_invite_clicked', { source: 'mobile_menu' });
  goToPath(AdminRouteNames.ReferralProgram);
};

// Pages that should NOT show expanding tabs in the mobile drawer
const mobileNoExpandPages = new Set<string>([
  AdminRouteNames.Shop,
  AdminRouteNames.LoyaltyProgram,
]);

const hasMobileTabs = (menuName: string) => {
  const pageConfig = adminPageConfigMap.get(menuName);
  return pageConfig?.tabs?.length && !mobileNoExpandPages.has(menuName);
};

const handleMobileMenuClick = (menu: IMenuItem) => {
  if (hasMobileTabs(menu.name)) {
    mobileExpandedMenu.value = mobileExpandedMenu.value === menu.name ? null : menu.name;
  } else {
    goToPath(menu.name as AdminRouteNames);
  }
};

const goToPathWithTab = (pathName: AdminRouteNames, tabIndex: number) => {
  router.push({ name: pathName, query: { tab: String(tabIndex) } });
  setPage(pathName);
  mobileExpandedMenu.value = null;
  drawerClicked();
};

const checkAndShowWelcomeModal = async () => {
  try {
    const hasSeenModal = await getValue(WELCOME_MODAL_STORAGE_KEY);
    if (!hasSeenModal) {
      setTimeout(() => {
        showWelcomeModal.value = true;
      }, 2000);
    } else {
      // Welcome modal already dismissed in a previous session — the tour can
      // start right away (covers owners who registered before the tour existed).
      maybeAutoStartTour();
    }
  } catch (error) {
    console.error('Error checking welcome modal status:', error);
  }
};

const handleWelcomeModalClose = async () => {
  const { setValue } = useStorage();
  try {
    await setValue(WELCOME_MODAL_STORAGE_KEY, 'true');
    showWelcomeModal.value = false;
    // Kick off the guided product tour right after the welcome modal.
    maybeAutoStartTour();
  } catch (error) {
    console.error('Error saving welcome modal status:', error);
  }
};

const runMethodWithParams = (methodName: string) => {
  if (methodName === 'addNewShopItem') addNewShopItem();
  else if (methodName === 'openNotifications') openNotifications();
  else if (methodName === 'refresh') refreshData();
  else if (methodName === 'goBackToClients') navigateToParent();
  else if (methodName === 'logout') signOut();
  else if (methodName === 'contact-us') contactUs();
};

const refreshData = async () => {
  isRefreshClicked.value = true;
  adminStore.fetchBusinessData();
  setTimeout(() => {
    isRefreshClicked.value = false;
  }, 350);
};

const openTrialModal = () => {
  openDrawer(TrialInfo, { onClose: closeTrialModal }, { fullMobile: true, closable: false, noPadding: true });
};
const openNotifications = () => {
  trackEvent('notifications_opened');
  openDrawer(NotificationsPanel, {}, { title: 'התראות', fullMobile: true });
};
const addNewShopItem = () => {
  trackEvent('shop_add_product_clicked');
  openDrawer(ShopAddProduct, { onClose: closeDrawer }, { title: 'הוספת מוצר חדש' });
};

// Never while impersonating: it would copy the business's customer list onto the
// platform admin's phone. Also skipped for users who can't see customer contact details.
const callerId = useCallerId();
const syncCallerId = async (force = false) => {
  if (isImpersonating.value || !callerId.isAvailable) return;
  try {
    // The owner revoked this employee's access to contact details: drop the cached list.
    if (!adminStore.getCanViewCustomerContact) {
      if ((await callerId.loadSettings()).enabled) await callerId.disable();
      return;
    }
    await callerId.sync({ force });
  } catch (e) {
    console.error(e);
  }
};

const isNativeApp = Capacitor.isNativePlatform();

const signOut = async () => {
  trackEvent('logout_clicked');
  stopIncomingCallLive();
  resetPostHog();
  showSpinner.value = true;
  // Wipe this business's customer directory from the device before handing it to the next login.
  await callerId.clearOnLogout().catch(e => console.error(e));
  await Session.signOut();
  sharedStore.setIsDrawerOpen(false);
  await router.push({ name: AuthRouteNames.AdminLogin });
};

const checkForSession = async () => {
  if (!(await Session.doesSessionExist())) {
    await router.push({ name: AuthRouteNames.AdminLogin });
    return false;
  }
  const accessTokenPayload = await Session.getAccessTokenPayloadSecurely();
  const currentUserId = accessTokenPayload?._id;
  adminStore.updateSession(currentUserId);
  // Use clientRole from token if available, fall back to mainClient boolean for backward compat
  if (accessTokenPayload.clientRole) {
    setClientRole(accessTokenPayload.clientRole);
  } else {
    setIsMainClient(accessTokenPayload.mainClient);
  }
  isImpersonating.value = !!accessTokenPayload.isImpersonation;
  impersonatedByEmail.value = accessTokenPayload.impersonatorEmail ?? null;
  session.value = true;
  return true;
};

const stopImpersonation = async () => {
  if (stoppingImpersonation.value) return;
  stoppingImpersonation.value = true;
  try {
    await PlatformApi.stopImpersonation();
    // Hard reload back to the platform panel with the restored admin session.
    window.location.assign('/platform-panel');
  } catch (e) {
    console.error(e);
    stoppingImpersonation.value = false;
  }
};

const contactUs = () => {
  trackEvent('contact_us_clicked');
  sendWhatsApp('972559299380', 'היי, הגעתי דרך האפליקציה ואשמח לעזרה.');
};

const checkShouldShowNewVersionModal = async () => {
  if (Capacitor.isNativePlatform()) {
    const { checkIfAppUpdateAvailable } = useAppUpdate();
    const isUpdateAvailable = await checkIfAppUpdateAvailable();
    if (isUpdateAvailable) {
      openDrawer(VersionUpdateDrawerContent, {}, { closable: false });
    }
  }
};

const closeTrialModal = async () => {
  closeDrawer();
  if (Capacitor.isNativePlatform())
    await Preferences.set({ key: 'saw-trial-modal', value: 'true' });
  else
    localStorage.setItem('saw-trial-modal', 'true');
};

const onLoad = async () => {
  const isSessionExist = await checkForSession();
  if (isSessionExist) {
    try {
      await adminStore.fetchBusinessData();
      const businessData = adminStore.getBusinessSiteData;
      const isTrial = businessData?.contract?.status === 'trial';
      // While a platform admin is impersonating, never re-identify this browser
      // as the business owner — it would attribute support activity to the
      // owner and pollute the platform's engagement metrics. Events still fire,
      // tagged so impersonated traffic can be filtered out.
      if (!isImpersonating.value) {
        identifyUser(adminStore.getUserId, {
          business_title: adminStore.getBusinessTitle,
          bid: businessData?.bid,
          is_main_client: adminStore.getMainClient,
          platform: Capacitor.getPlatform(),
          plan: businessData?.plan,
          is_trial: isTrial,
        });
        setBusinessGroup(businessData?.bid, {
          name: adminStore.getBusinessTitle,
          plan: businessData?.plan,
          is_trial: isTrial,
          providers_count: adminStore.getProviders?.length,
          services_count: adminStore.getServices?.length,
        });
      }
      trackEvent('dashboard_loaded', {
        business_title: adminStore.getBusinessTitle,
        bid: businessData?.bid,
        platform: Capacitor.getPlatform(),
        is_impersonation: isImpersonating.value,
      });
      setPage(AdminRouteNames.Home);
      showSpinner.value = false;
      await checkForAnnouncements();
    } catch (err) {
      console.error(err);
      if (err === 'Business does not exist') {
        console.log('Business does not exist, need to complete registration.');
        router.push({ name: AuthRouteNames.Register, query: { step: 'business' } });
        return;
      } else {
        showSpinner.value = false;
      }
    }
    getResourcesUsageData().then().catch();
    // Desktop/web: listen for calls the owner's Android phone recognises. Not while a
    // platform admin impersonates, and not for users who can't see customer contacts.
    if (!isNativeApp && !isImpersonating.value && adminStore.getCanViewCustomerContact)
      startIncomingCallLive();
    automationsStore.fetchNudgeCount();
    automationsStore.fetchAutomationConfigs();
    tasksStore.fetchCounts();
    await adminClientsStore.fetchBusinessCustomers().then().catch();
  } else {
    return;
  }

  if (Capacitor.isNativePlatform()) {
    console.log('dayJoined: ', adminStore.businessSiteData.createdAt);

    // Everything below binds this *device* to the logged-in identity. While a
    // platform admin is impersonating, that identity is the business owner, so
    // we skip it entirely: registering here would subscribe the admin's phone
    // to the business's push notifications (which carry customer names and
    // appointment details) and would keep doing so after exiting impersonation,
    // since nothing logs OneSignal back out. Rate prompts and app-version
    // reporting are skipped for the same reason — they'd write the admin's
    // device state onto the owner's record.
    if (!isImpersonating.value) {
      const { handleRateAppPopup } = useRate({ dayJoined: adminStore.businessSiteData!.createdAt! as string });
      handleRateAppPopup();
      checkShouldShowNewVersionModal();

      // Report app version to backend
      CapacitorApp.getInfo()
        .then(info => SharedApi.reportAppVersion(info.version))
        .catch(e => console.error('Failed to report app version:', e));

      console.log('in notifications block');
      OneSignal.initialize(import.meta.env.VITE_ONESIGNAL_APP_ID || '');
      OneSignal.Debug.setLogLevel(6);
      OneSignal.Notifications.requestPermission().then((res) => {
        if (res) {
          console.log('adminStore.getUserId', adminStore.getUserId);
          OneSignal.login(adminStore.getUserId);
          OneSignal.User.addTag('bid', adminStore?.getBusinessSiteData?.bid || '');
          OneSignal.User.addTag('type', 'business');

          const welcomeSentKey = 'welcomeNotificationSent';
          getValue(welcomeSentKey).then((val) => {
            if (!val) {
              SharedApi.sendWelcomeNotification()
                .then(() => setValue(welcomeSentKey, 'true'))
                .catch((e: any) => console.error(e));
            }
          });
        }
      });

      // Foreground: notification arrives while app is open
      OneSignal.Notifications.addEventListener('foregroundWillDisplay', () => {
        adminStore.getNotifications();
        adminHomeStore.fetchBusinessCalendarDataSilently();
      });

      // Foreground tap: user taps notification banner while app is open
      OneSignal.Notifications.addEventListener('click', (event: any) => {
        adminStore.getNotifications();
        adminHomeStore.fetchBusinessCalendarDataSilently();
        handleNotificationTap(event?.notification?.additionalData);
      });
    }

    // Background tap: user taps notification from tray → app resumes.
    // The OneSignal click event fires before our listener is registered in
    // this case, so appStateChange is the reliable catch-all.
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        adminStore.getNotifications();
        adminHomeStore.fetchBusinessCalendarDataSilently();
        syncCallerId();
      }
    });

    // Caller ID: refresh the on-device customer directory (no-op unless the user turned it on
    // in settings; throttled inside the composable).
    syncCallerId();
    // …and right after an appointment is booked/edited/cancelled here.
    onAppointmentsChanged(() => syncCallerId(true));
  } else {
    const {
      init: oneSignalInit,
      showSlidedownPrompt,
      showNativePrompt,
      sendTag,
      setExternalUserId
    } = useOneSignal();

    // Skipped while impersonating — see the native branch above: this would
    // bind the admin's browser to the business owner's push identity.
    if (!isImpersonating.value) {
      oneSignalInit({
        appId: import.meta.env.VITE_ONESIGNAL_APP_ID || '',
        allowLocalhostAsSecureOrigin: import.meta.env.MODE === 'development'
      })
        .then(async () => {
          await sendTag('bid', adminStore?.getBusinessSiteData?.bid || '');
          await setExternalUserId(adminStore?.userId);
          setTimeout(async () => {
            await showSlidedownPrompt();
            await showNativePrompt();
          }, 15000);
        })
        .catch(() => console.error('Failed to init onesignal'));
    }

    if (isMobile.value)
      setTimeout(() => {
        showDownloadApp.value = true;
      }, 20000);
  }
};

// "What's new" updates (date-based, main user only — the server decides). Never while a
// platform admin is impersonating: dismissing would mark them seen for the real owner.
const checkForAnnouncements = async () => {
  if (isImpersonating.value) return;
  try {
    const announcements = await AnnouncementService.getActiveAnnouncements();
    if (announcements.length) {
      unseenAnnouncements.value = announcements;
      showWhatsNew.value = true;
    }
  } catch (error) {
    console.error('Failed to load announcements:', error);
  }
};

const handleAnnouncementsDismiss = async () => {
  const ids = unseenAnnouncements.value.map(a => a._id).filter((id): id is string => !!id);
  showWhatsNew.value = false;
  unseenAnnouncements.value = [];
  if (ids.length) await AnnouncementService.markSeen(ids).catch(e => console.error('Failed to mark announcements seen:', e));
};


// Whenever the admin returns to the foreground, refresh the appointments (and
// notifications) so a stale calendar never lingers. Native resume is handled via
// Capacitor's appStateChange (see the push setup); on web we listen for the tab
// becoming visible again (re-entering the tab, un-minimizing the window).
const refreshOnForeground = () => {
  getNotifications();
  adminHomeStore.fetchBusinessCalendarDataSilently();
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') refreshOnForeground();
};

onMounted(async () => {
  await onLoad();
  checkAndShowWelcomeModal();
  if (!Capacitor.isNativePlatform())
    document.addEventListener('visibilitychange', handleVisibilityChange);
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  onAppointmentsChanged(undefined);
  stopIncomingCallLive();
});

setInterval(() => {
  if (![...Object.values(CustomerRouteNames), ...Object.values(AuthRouteNames)].includes(currentPage.value as AuthRouteNames | CustomerRouteNames))
    getNotifications();
}, 30 * 1000);

watch(
  () => route.query.modal,
  (newId) => {
    if (newId === 'close') {
      closeDrawer();
    }
  }
);
</script>

<style lang="scss" scoped>
@use '../../assets/css/vars' as *;

/* ── Referral card with animated gradient border (mobile menu) ── */
.referral-card {
  position: relative;
  width: 100%;
  margin-top: 1rem;
  padding: 2px;
  border-radius: 14px;
  cursor: pointer;
  background: conic-gradient(
    from var(--referral-angle, 0deg),
    #8864ee,
    #ec4899,
    #f59e0b,
    #22c55e,
    #06b6d4,
    #8864ee
  );
  animation:
    referralSpin 4s linear infinite,
    referralFloat 3.2s ease-in-out infinite;
  box-shadow: 0 10px 24px rgba(136, 100, 238, 0.3);
}

@keyframes referralFloat {
  0%, 100% {
    transform: translateY(0);
    box-shadow: 0 10px 24px rgba(136, 100, 238, 0.3);
  }
  50% {
    transform: translateY(-6px);
    box-shadow: 0 16px 28px rgba(136, 100, 238, 0.35);
  }
}

.referral-card__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

@property --referral-angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@keyframes referralSpin {
  to { --referral-angle: 360deg; }
}

/* Fallback for browsers without @property support — rotate a pseudo-element instead */
@supports not (background: conic-gradient(from 0deg, red, blue)) {
  .referral-card {
    background: transparent;
    animation: none;
  }
  .referral-card::before {
    content: '';
    position: absolute;
    inset: -50%;
    background: conic-gradient(#8864ee, #ec4899, #f59e0b, #22c55e, #06b6d4, #8864ee);
    animation: referralSpinFallback 4s linear infinite;
    z-index: 0;
  }
  .referral-card__inner {
    position: relative;
    z-index: 1;
  }
}

@keyframes referralSpinFallback {
  to { transform: rotate(360deg); }
}

/* ── Right drawer (mobile only) ── */
.right-drawer {
  position: absolute;
  right: -$drawer-width;
  width: $drawer-width;
  height: 100%;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: start;
  padding: 1.25rem 1rem;
  gap: 0.15rem;
  overflow-y: auto;
  z-index: 200;

  &.drawer-open {
    right: 0;
  }
}

/* ── Page panel (mobile) ── */
.page {
  position: absolute;
  right: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  transition: all 0.2s;

  &.drawer-open {
    right: $drawer-width;
    pointer-events: none;
    transform: scale(0.97);
    border-radius: 1rem;
    overflow: hidden;
  }
}

/* ── Desktop: page fills remaining flex space ── */
@media (min-width: 768px) {
  .page {
    position: relative !important;
    right: auto !important;
    transform: none !important;
    border-radius: 0.75rem !important;
    flex: 1;
    pointer-events: auto !important;
    overflow: hidden;
    margin: 0.5rem 0.5rem 0.5rem 0;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.4);
  }
}

/* ── Rotating icon animation ── */
@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating {
  transform-origin: center;
  animation: rotating 1s linear infinite;
}
</style>
